/**
 * Unity's two per-particle curve types, evaluated the way Unity evaluates
 * them, for the modules that ride on a particle's normalised age.
 *
 * Nothing here belongs to one system: `iceBurst.ts` (the break shower) and
 * `iceCountPuff.ts` (the countdown puff) both read `SizeModule` /
 * `ColorModule` / `RotationModule` curves out of the same prefab, and these
 * are the evaluators they share. The keys are carried exactly as the YAML
 * stores them — `time`, `value`, `inSlope`, `outSlope` — so a test can hold
 * the numbers against the prefab without any translation in between.
 */

/** One `m_Curve` entry of a Unity `AnimationCurve`, as the prefab writes it. */
export interface CurveKey {
  readonly time: number
  readonly value: number
  readonly inSlope: number
  readonly outSlope: number
}

/** One colour key of a Unity `Gradient`: `ctimeN / 65535` and `keyN.rgb`. */
export interface ColourKey {
  readonly time: number
  readonly rgb: readonly [number, number, number]
}

/** One alpha key of a Unity `Gradient`: `atimeN / 65535` and `keyN.a`. */
export interface AlphaKey {
  readonly time: number
  readonly alpha: number
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/**
 * An `AnimationCurve` at `t`: cubic Hermite between neighbouring keys using
 * the stored `outSlope` of the left key and `inSlope` of the right, and
 * clamped to the end keys outside `[first, last]` — which is what Unity's
 * default `m_PreInfinity` / `m_PostInfinity` of 2 (ClampForever) does.
 *
 * Slopes are per unit of `time`, so over a segment of length `dt` the Hermite
 * tangents are `slope × dt`. A curve with no keys evaluates to 1, Unity's
 * behaviour for a module whose curve was never edited.
 */
export function curveAt(keys: readonly CurveKey[], t: number): number {
  if (keys.length === 0) return 1
  if (t <= keys[0].time) return keys[0].value
  const last = keys[keys.length - 1]
  if (t >= last.time) return last.value
  let i = 0
  while (i + 1 < keys.length && keys[i + 1].time <= t) i++
  const k0 = keys[i]
  const k1 = keys[i + 1]
  const dt = k1.time - k0.time
  const u = (t - k0.time) / dt
  const u2 = u * u
  const u3 = u2 * u
  const m0 = k0.outSlope * dt
  const m1 = k1.inSlope * dt
  return (
    (2 * u3 - 3 * u2 + 1) * k0.value +
    (u3 - 2 * u2 + u) * m0 +
    (-2 * u3 + 3 * u2) * k1.value +
    (u3 - u2) * m1
  )
}

/**
 * A `Gradient` in Blend mode (`m_Mode: 0`) at `t`: linear between neighbouring
 * keys, held flat beyond the first and last. Colour keys and alpha keys are
 * separate lists with separate times, as Unity stores them.
 */
export function gradientAt(
  colour: readonly ColourKey[],
  alpha: readonly AlphaKey[],
  t: number,
): { rgb: [number, number, number]; alpha: number } {
  const rgb: [number, number, number] = [0, 0, 0]
  for (let c = 0; c < 3; c++) {
    rgb[c] = sampleKeys(
      colour.map((k) => k.time),
      colour.map((k) => k.rgb[c]),
      t,
    )
  }
  return {
    rgb,
    alpha: sampleKeys(
      alpha.map((k) => k.time),
      alpha.map((k) => k.alpha),
      t,
    ),
  }
}

function sampleKeys(times: readonly number[], values: readonly number[], t: number): number {
  if (t <= times[0]) return values[0]
  const n = times.length
  if (t >= times[n - 1]) return values[n - 1]
  let i = 0
  while (i + 1 < n && times[i + 1] <= t) i++
  const span = times[i + 1] - times[i]
  return span <= 0 ? values[i + 1] : lerp(values[i], values[i + 1], (t - times[i]) / span)
}
